import React, { useMemo, useState } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import Card from './components/Card';
import Button from './components/Button';
import { launchVisualAudit } from './api';
import { useToast } from './ToastContext';
import { Eye, Image as ImageIcon, AlertTriangle, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';
import ReferencePagesBanner from './components/ReferencePagesBanner';
import ReferencePageBadge from './components/ReferencePageBadge';
import AuditEmptyState from './components/AuditEmptyState';
import { isReferencePage } from './utils/referencePages';
import { getScoreQualitative } from './utils/scoreLabels';

function scoreBadgeClass(score) {
  const q = getScoreQualitative(score);
  return `${q.bg} ${q.color}`.replace('text-blue-gray-400', 'text-blue-gray-500');
}

function scoreColor(score) {
  return getScoreQualitative(score).color;
}

function noteColor(note) {
  if (note >= 4) return 'bg-green-500';
  if (note >= 3) return 'bg-amber-400';
  return 'bg-red-400';
}

function criterionCardStyle(note) {
  if (note >= 4) return 'border-green-200 bg-green-50/50';
  if (note >= 3) return 'border-amber-200 bg-amber-50/50';
  return 'border-red-200 bg-red-50/40';
}

function PageAuditCard({ va, project, defaultExpanded = false }) {
  const isRef = isReferencePage(va?.url, project);
  const [expanded, setExpanded] = useState(defaultExpanded);

  if (va.error) {
    return (
      <Card className="overflow-hidden">
        <div className="flex items-center justify-between gap-3 px-4 py-3 bg-blue-gray-50 border-b border-blue-gray-200">
          <p className="text-xs font-mono text-blue-gray-600 truncate flex-1 flex items-center gap-2 min-w-0">
            <span className="truncate">{va.url}</span>
          </p>
          <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-600 font-medium flex-shrink-0">Erreur</span>
        </div>
        <div className="p-4 text-sm text-red-600">{va.error}</div>
      </Card>
    );
  }

  const a = va.analysis || {};
  const score = a.score_visuel;
  const sortedCriteres = Array.isArray(a.criteres)
    ? [...a.criteres].sort((x, y) => Number(x?.note || 0) - Number(y?.note || 0))
    : [];

  return (
    <Card className={`overflow-hidden ${isRef ? 'ring-2 ring-accent/25 border-accent/30' : ''}`}>
      {/* Header */}
      <div
        className={`flex items-center justify-between gap-3 px-4 py-3 border-b border-blue-gray-200 cursor-pointer select-none ${isRef ? 'bg-accent/[0.06]' : 'bg-blue-gray-50'}`}
        onClick={() => setExpanded((e) => !e)}
      >
        <p className="text-xs font-mono text-blue-gray-600 truncate flex-1 flex items-center gap-2 min-w-0">
          <span className="truncate">{va.url}</span>
          {isRef && <ReferencePageBadge compact />}
        </p>
        <div className="flex items-center gap-2 flex-shrink-0">
          {score !== undefined && (
            <span className={`text-sm font-bold px-2.5 py-0.5 rounded-full ${scoreBadgeClass(score)}`}>
              {score}/100
            </span>
          )}
          {expanded ? <ChevronUp size={15} className="text-blue-gray-400" /> : <ChevronDown size={15} className="text-blue-gray-400" />}
        </div>
      </div>

      {expanded && (
        <div className="flex flex-col lg:flex-row min-h-0">
          {/* Left: screenshot full height */}
          <div className="lg:w-[340px] xl:w-[400px] flex-shrink-0 border-b lg:border-b-0 lg:border-r border-blue-gray-200 bg-blue-gray-50 flex flex-col">
            {va.screenshot_b64 ? (
              <div className="overflow-y-auto" style={{ maxHeight: '600px' }}>
                <img
                  src={`data:image/png;base64,${va.screenshot_b64}`}
                  alt={`Capture ${va.url}`}
                  className="w-full block"
                  style={{ imageRendering: 'auto' }}
                />
              </div>
            ) : (
              <div className="flex-1 min-h-[200px] flex items-center justify-center text-blue-gray-400 text-xs gap-1.5">
                <ImageIcon size={16} />
                Pas d&apos;image disponible
              </div>
            )}
            <div className="px-3 py-2 border-t border-blue-gray-200 bg-white">
              <p className="text-[10px] text-blue-gray-400 text-center">Capture page complète (desktop 1440px)</p>
            </div>
          </div>

          {/* Right: full analysis */}
          <div className="flex-1 min-w-0 p-5 space-y-5 overflow-y-auto" style={{ maxHeight: '600px' }}>

            {/* Score + résumé */}
            <div>
              {score !== undefined && (
                <div className="flex items-center gap-2 mb-3">
                  <span className={`text-3xl font-bold ${scoreColor(score)}`}>{score}</span>
                  <span className="text-sm text-blue-gray-400">/100</span>
                  <span className="text-sm text-blue-gray-500 ml-1">Score visuel global</span>
                </div>
              )}
              {a.resume && (
                <p className="text-sm text-blue-gray-700 leading-relaxed">{a.resume}</p>
              )}
            </div>

            {/* Critères */}
            {sortedCriteres.length > 0 && (
              <div>
                <div className="flex items-center justify-between gap-2 mb-3">
                  <p className="text-xs font-semibold text-blue-gray-500 uppercase tracking-wide">Analyse par critère</p>
                  <span className="text-[11px] text-blue-gray-400">Statique uniquement · trié du plus critique au plus solide</span>
                </div>
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-2.5">
                  {sortedCriteres.map((c) => (
                    <div key={c.cle} className={`border rounded-lg p-3 space-y-2 ${criterionCardStyle(c.note)}`}>
                      <div className="flex items-start justify-between gap-2">
                        <span className="text-sm font-semibold text-blue-gray-700 leading-tight">{c.nom}</span>
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          <div className="flex gap-0.5">
                            {[1, 2, 3, 4, 5].map((n) => (
                              <div
                                key={n}
                                className={`w-2.5 h-2.5 rounded-full ${n <= c.note ? noteColor(c.note) : 'bg-blue-gray-200'}`}
                              />
                            ))}
                          </div>
                          <span className="text-xs font-semibold text-blue-gray-500 w-6 text-right">{c.note}/5</span>
                        </div>
                      </div>
                      {c.observation && (
                        <div className="rounded-md bg-white/80 border border-white px-2.5 py-2">
                          <p className="text-[10px] font-semibold uppercase tracking-wide text-blue-gray-400 mb-1">
                            Point expliqué
                          </p>
                          <p className="text-xs text-blue-gray-600 leading-relaxed">{c.observation}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Points forts / Axes */}
            {(a.points_forts?.length > 0 || a.axes_amelioration?.length > 0) && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-3 border-t border-blue-gray-100">
                {a.points_forts?.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-green-700 mb-2">Points forts</p>
                    <ul className="space-y-1.5">
                      {a.points_forts.map((p, i) => (
                        <li key={i} className="text-xs text-blue-gray-600 flex gap-1.5 leading-relaxed">
                          <span className="text-green-500 mt-0.5 flex-shrink-0">✓</span>
                          <span>{p}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {a.axes_amelioration?.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-amber-700 mb-2">À améliorer</p>
                    <ul className="space-y-1.5">
                      {a.axes_amelioration.map((ax, i) => (
                        <li key={i} className="text-xs text-blue-gray-600 flex gap-1.5 leading-relaxed">
                          <span className="text-amber-500 mt-0.5 flex-shrink-0">→</span>
                          <span>{ax}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Recommandation prioritaire */}
            {a.recommandation_prioritaire && (
              <div className="p-3.5 bg-accent/8 border border-accent/20 rounded-lg">
                <p className="text-xs font-semibold text-accent mb-1">Recommandation prioritaire</p>
                <p className="text-sm text-blue-gray-700 leading-relaxed">{a.recommandation_prioritaire}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}

export default function ProjectVisualAudit() {
  const navigate = useNavigate();
  const { slug } = useParams();
  const { project, refreshProject } = useOutletContext();
  const { showToast } = useToast();
  const [isRelaunching, setIsRelaunching] = useState(false);

  const pages = project?.visual_audit?.pages || [];
  const okPages = useMemo(() => pages.filter((p) => !p?.error && p?.analysis), [pages]);
  const failedPages = useMemo(() => pages.filter((p) => !!p?.error), [pages]);

  const averageScore = useMemo(() => {
    const scores = okPages
      .map((p) => Number(p?.analysis?.score_visuel))
      .filter((n) => Number.isFinite(n));
    if (!scores.length) return null;
    return Math.round(scores.reduce((sum, n) => sum + n, 0) / scores.length);
  }, [okPages]);

  const urlsToAudit = useMemo(() => {
    const fromReference = Array.isArray(project?.reference_pages)
      ? project.reference_pages.filter((u) => typeof u === 'string' && u.trim())
      : [];
    if (fromReference.length) return fromReference.slice(0, 6);

    const fromExistingVisual = pages
      .map((p) => p?.url)
      .filter((u) => typeof u === 'string' && u.trim());
    if (fromExistingVisual.length) return fromExistingVisual.slice(0, 6);

    if (project?.url && typeof project.url === 'string') return [project.url.trim()];
    return [];
  }, [project?.reference_pages, project?.url, pages]);

  const sortedPages = useMemo(() => {
    const ref = pages.filter((p) => isReferencePage(p?.url, project));
    const other = pages.filter((p) => !isReferencePage(p?.url, project));
    return [...ref, ...other];
  }, [pages, project]);

  const handleRelaunchVisualAudit = async () => {
    if (!slug || urlsToAudit.length === 0) {
      showToast("Aucune URL disponible pour relancer l'audit visuel.", 'warning');
      return;
    }

    setIsRelaunching(true);
    try {
      await launchVisualAudit(slug, urlsToAudit);
      await refreshProject?.();
      showToast('Audit visuel relancé avec succès.', 'success');
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Erreur inconnue';
      showToast(`\u00c9chec de relance de l'audit visuel\u00a0: ${detail}`, 'error');
    } finally {
      setIsRelaunching(false);
    }
  };

  if (!pages.length) {
    return (
      <AuditEmptyState
        variant="centered"
        icon={Eye}
        title="Aucun audit visuel disponible"
        description="Définissez des pages de référence puis lancez l'audit visuel (étape 6 de l'audit complet) sur ces pages uniquement."
      />
    );
  }

  const scoreDistribution = useMemo(() => {
    const scores = okPages.map((p) => Number(p?.analysis?.score_visuel)).filter((n) => Number.isFinite(n));
    return {
      good: scores.filter((s) => s >= 75).length,
      mid: scores.filter((s) => s >= 50 && s < 75).length,
      bad: scores.filter((s) => s < 50).length,
      total: scores.length,
    };
  }, [okPages]);

  return (
    <div className="mx-auto w-full px-2 md:px-0 flex flex-col gap-6">
      <ReferencePagesBanner project={project} />

      {/* En-tête */}
      <Card>
        <div className="flex flex-wrap items-start gap-4 justify-between">
          <div>
            <h2 className="font-heading text-base font-semibold text-blue-gray-900">Audit visuel IA</h2>
            <p className="text-sm text-blue-gray-500 mt-0.5">
              Capture statique · design, typo, couleurs et composants (hors animations)
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            {averageScore !== null && (
              <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${scoreBadgeClass(averageScore)}`}>
                Score moyen {averageScore}/100
              </span>
            )}
            <Button
              variant="secondary"
              size="sm"
              onClick={handleRelaunchVisualAudit}
              disabled={isRelaunching || urlsToAudit.length === 0}
            >
              <RefreshCw size={13} className={`mr-1 ${isRelaunching ? 'animate-spin' : ''}`} />
              {isRelaunching ? 'Relance...' : "Relancer l'audit"}
            </Button>
          </div>
        </div>

        {/* Distribution des scores */}
        {scoreDistribution.total > 0 && (
          <div className="mt-4 pt-4 border-t border-blue-gray-100">
            <p className="text-[11px] font-semibold text-blue-gray-400 uppercase tracking-wide mb-3">
              Distribution · {scoreDistribution.total} page{scoreDistribution.total > 1 ? 's' : ''}
            </p>
            <div className="flex gap-3">
              {[
                { count: scoreDistribution.good, label: 'Bon ≥ 75', color: 'bg-green-500', text: 'text-green-600' },
                { count: scoreDistribution.mid, label: 'Moyen 50–74', color: 'bg-amber-400', text: 'text-amber-600' },
                { count: scoreDistribution.bad, label: 'Faible < 50', color: 'bg-red-400', text: 'text-red-500' },
              ].map(({ count, label, color, text }) => (
                <div key={label} className="flex items-center gap-2">
                  <div className={`w-2.5 h-2.5 rounded-full ${color}`} />
                  <span className={`text-sm font-bold ${text}`}>{count}</span>
                  <span className="text-xs text-blue-gray-400">{label}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>

      {failedPages.length > 0 && (
        <Card className="border-amber-200 bg-amber-50">
          <div className="flex items-start gap-2">
            <AlertTriangle size={16} className="text-amber-700 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-amber-800">
              {failedPages.length} page{failedPages.length > 1 ? 's' : ''} n&apos;ont pas pu être analysée{failedPages.length > 1 ? 's' : ''}.
            </div>
          </div>
        </Card>
      )}

      <div className="space-y-4">
        {sortedPages.map((va, idx) => (
          <PageAuditCard
            key={va.url}
            va={va}
            project={project}
            defaultExpanded={idx === 0}
          />
        ))}
      </div>
    </div>
  );
}
